import React, { useEffect, useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { isoDate, prettifyStoreName, fmtKr, ALL_STORES } from "./workshopCapacityUtils";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const MONTH_NAMES = [
  "Januar", "Februar", "Mars", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Desember",
];

function daysInMonth(year, monthIndex0) {
  return new Date(year, monthIndex0 + 1, 0).getDate();
}

export default function WorkshopDelerListeTab() {
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState([]);
  const [monthOffset, setMonthOffset] = useState(0);
  const [storeSlug, setStoreSlug] = useState("alle");

  const today = useMemo(() => new Date(), []);
  const monthDate = useMemo(
    () => new Date(today.getFullYear(), today.getMonth() + monthOffset, 1),
    [today, monthOffset]
  );
  const year = monthDate.getFullYear();
  const monthIndex0 = monthDate.getMonth();
  const isCurrentMonth = monthOffset === 0;

  const firstDayISO = useMemo(() => isoDate(new Date(year, monthIndex0, 1)), [year, monthIndex0]);
  const lastDayISO = useMemo(
    () => isoDate(new Date(year, monthIndex0, daysInMonth(year, monthIndex0))),
    [year, monthIndex0]
  );

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      try {
        const responses = await Promise.all(
          ALL_STORES.map((slug) =>
            fetch(
              `${API_BASE_URL}/workshop/capacity-history?verkstedSlug=${encodeURIComponent(slug)}&from=${firstDayISO}&to=${lastDayISO}`
            ).then((r) => (r.ok ? r.json() : { details: [] }))
          )
        );
        setDetails(responses.flatMap((r) => (r.details || []).filter((d) => !d.reason)));
      } catch (err) {
        console.error("Feil ved henting av deleliste:", err);
        setDetails([]);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [firstDayISO, lastDayISO]);

  // Alle ordinære delelinjer for valgt måned/verksted, uavhengig av hvilken service de
  // tilhører. Garanti/reklamasjon-deler dokumenteres allerede under Reklamasjon sykkel
  // og holdes derfor helt utenfor denne listen.
  const partsRows = useMemo(() => {
    return details
      .filter((d) => storeSlug === "alle" || d.butikk === storeSlug)
      .flatMap((d) => d.partsLines || [])
      .filter((p) => !p.isWarranty);
  }, [details, storeSlug]);

  // Slå sammen deler med samme EAN (eller produktnavn når EAN mangler) til én rad
  const groupedRows = useMemo(() => {
    const map = new Map();
    for (const p of partsRows) {
      const key = p.ean
        ? `ean:${p.ean}`
        : `navn:${String(p.product || "").trim().toLowerCase()}`;

      if (!map.has(key)) {
        map.set(key, {
          key,
          product: p.product || "Del",
          brand: p.brand || "",
          ean: p.ean || "",
          qty: 0,
          verdi: 0,
        });
      }
      const g = map.get(key);
      g.qty += Number(p.qty) || 0;
      g.verdi += Number(p.lineTotal) || 0;
    }
    return Array.from(map.values()).sort((a, b) => b.verdi - a.verdi);
  }, [partsRows]);

  const summary = useMemo(() => {
    let verdi = 0;
    let antallStk = 0;
    for (const p of partsRows) {
      antallStk += Number(p.qty) || 0;
      verdi += Number(p.lineTotal) || 0;
    }
    return {
      unikeDeler: groupedRows.length,
      antallStk,
      verdi,
    };
  }, [partsRows, groupedRows]);

  if (loading) return <div className="py-10 text-center">Laster deleliste…</div>;

  return (
    <div className="space-y-6">
      {/* Header med måneds- og butikkvalg */}
      <div className="rounded border bg-green-50 p-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-sm text-gray-600">Deler brukt på ferdige servicer</div>
            <div className="text-xl font-bold">{MONTH_NAMES[monthIndex0]} {year}</div>
            <div className="text-sm text-gray-700 mt-1">{firstDayISO} – {lastDayISO}</div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <select
              value={storeSlug}
              onChange={(e) => setStoreSlug(e.target.value)}
              className="border rounded px-2 py-1.5 text-sm bg-white"
            >
              <option value="alle">Alle verksteder</option>
              {ALL_STORES.map((slug) => (
                <option key={slug} value={slug}>{prettifyStoreName(slug)}</option>
              ))}
            </select>
            <button
              onClick={() => setMonthOffset((o) => o - 1)}
              className="px-3 py-1.5 rounded border bg-white hover:bg-gray-50 text-sm"
            >
              ← Forrige
            </button>
            {!isCurrentMonth && (
              <button
                onClick={() => setMonthOffset(0)}
                className="px-3 py-1.5 rounded border bg-white hover:bg-gray-50 text-sm text-green-700"
              >
                Denne måneden
              </button>
            )}
            <button
              onClick={() => setMonthOffset((o) => Math.min(0, o + 1))}
              disabled={isCurrentMonth}
              className="px-3 py-1.5 rounded border bg-white hover:bg-gray-50 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Neste →
            </button>
          </div>
        </div>
      </div>

      {/* Oppsummeringskort */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div className="rounded border p-4 bg-white">
          <div className="text-xs text-gray-500 uppercase tracking-wide">Unike deler</div>
          <div className="text-2xl font-bold mt-1">{summary.unikeDeler}</div>
        </div>
        <div className="rounded border p-4 bg-white">
          <div className="text-xs text-gray-500 uppercase tracking-wide">Antall stk</div>
          <div className="text-2xl font-bold mt-1">{summary.antallStk}</div>
        </div>
        <div className="rounded border p-4 bg-blue-50">
          <div className="text-xs text-gray-500 uppercase tracking-wide">Delesalg</div>
          <div className="text-2xl font-bold mt-1">{fmtKr(summary.verdi)}</div>
        </div>
      </div>

      {/* Deleliste */}
      <div className="overflow-x-auto">
        <table className="min-w-full border rounded bg-white text-sm">
          <thead className="bg-gray-100 text-gray-700">
            <tr>
              <th className="px-3 py-2 text-left">QR (EAN)</th>
              <th className="px-3 py-2 text-left">Del</th>
              <th className="px-3 py-2 text-left">Merke</th>
              <th className="px-3 py-2 text-left">EAN</th>
              <th className="px-3 py-2 text-right">Antall</th>
              <th className="px-3 py-2 text-right">Salgspris</th>
            </tr>
          </thead>
          <tbody>
            {groupedRows.length ? (
              groupedRows.map((g) => (
                <tr key={g.key} className="border-t hover:bg-gray-50 align-top">
                  <td className="px-3 py-2">
                    {g.ean ? (
                      <QRCodeSVG value={g.ean} size={44} level="M" />
                    ) : (
                      <span className="text-gray-400 text-xs">Ingen EAN</span>
                    )}
                  </td>
                  <td className="px-3 py-2">{g.product}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-600">{g.brand || "—"}</td>
                  <td className="px-3 py-2 whitespace-nowrap font-mono text-xs text-gray-600">{g.ean || "—"}</td>
                  <td className="px-3 py-2 text-right">{g.qty}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap font-medium">{fmtKr(g.verdi)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-gray-500">
                  Ingen deler registrert denne måneden
                </td>
              </tr>
            )}
          </tbody>
          {groupedRows.length > 0 && (
            <tfoot className="border-t bg-gray-50 font-semibold">
              <tr>
                <td colSpan={4} className="px-3 py-2">Totalt</td>
                <td className="px-3 py-2 text-right">{summary.antallStk}</td>
                <td className="px-3 py-2 text-right">{fmtKr(summary.verdi)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
